import React, { useState, useEffect } from 'react';
import { X, Loader, Sparkles, Calendar, Hash, Type, ToggleLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * ParameterInputModal - Modal for collecting user parameters before executing queries.
 * Consolidates common parameters (start_date, end_date) into single inputs.
 */
export const ParameterInputModal = ({
    isOpen,
    onClose,
    onExecute,
    queries,
    loading = false
}) => {
    const { t } = useTranslation('agents-modals');
    const [parameters, setParameters] = useState({});
    const [consolidatedParams, setConsolidatedParams] = useState({
        dates: { startDate: '', endDate: '' },
        strings: {},
        numbers: {},
        booleans: {}
    });

    // Extract and consolidate all parameters from queries
    useEffect(() => {
        if (!isOpen || !queries || queries.length === 0) return;

        const allParams = {};
        const consolidated = {
            dates: { startDate: '', endDate: '' },
            strings: {},
            numbers: {},
            booleans: {}
        };

        // Collect all unique parameters
        queries.forEach(query => {
            const queryParams = query.parameters || [];
            if (!Array.isArray(queryParams)) return;
            
            queryParams.forEach(param => {
                if (!param || !param.name) return;
                
                if (!allParams[param.name]) {
                    allParams[param.name] = {
                        ...param,
                        queries: []
                    };
                }
                allParams[param.name].queries.push(query.name);

                // Set default value
                if (param.default !== undefined && param.default !== null) {
                    if (param.type === 'date') {
                        if (param.name.includes('start') || param.name.includes('from')) {
                            consolidated.dates.startDate = param.default;
                        } else if (param.name.includes('end') || param.name.includes('to')) {
                            consolidated.dates.endDate = param.default;
                        }
                    } else if (param.type === 'boolean') {
                        consolidated.booleans[param.name] = param.default;
                    } else if (param.type === 'integer' || param.type === 'float') {
                        consolidated.numbers[param.name] = param.default;
                    } else {
                        consolidated.strings[param.name] = param.default;
                    }
                }
            });
        });

        setParameters(allParams);
        setConsolidatedParams(consolidated);
    }, [isOpen, queries]);

    const handleDateChange = (field, value) => {
        setConsolidatedParams(prev => ({
            ...prev,
            dates: {
                ...prev.dates,
                [field]: value
            }
        }));
    };

    const handleStringChange = (name, value) => {
        setConsolidatedParams(prev => ({
            ...prev,
            strings: {
                ...prev.strings,
                [name]: value
            }
        }));
    };

    const handleNumberChange = (name, value) => {
        setConsolidatedParams(prev => ({
            ...prev,
            numbers: {
                ...prev.numbers,
                [name]: value
            }
        }));
    };

    const handleBooleanChange = (name, checked) => {
        setConsolidatedParams(prev => ({
            ...prev,
            booleans: {
                ...prev.booleans,
                [name]: checked
            }
        }));
    };

    const handleExecute = () => {
        // Build parameters by query
        const parametersByQuery = {};
        
        queries.forEach(query => {
            const queryParams = query.parameters || [];
            const queryParamsObj = {};

            queryParams.forEach(param => {
                let value;
                
                // Get value from consolidated params
                if (param.type === 'date') {
                    if (param.name.includes('start') || param.name.includes('from')) {
                        value = consolidatedParams.dates.startDate;
                    } else if (param.name.includes('end') || param.name.includes('to')) {
                        value = consolidatedParams.dates.endDate;
                    }
                } else if (param.type === 'boolean') {
                    value = consolidatedParams.booleans[param.name];
                } else if (param.type === 'integer' || param.type === 'float') {
                    value = consolidatedParams.numbers[param.name];
                } else {
                    value = consolidatedParams.strings[param.name];
                }

                // Use default if no value provided
                if (value === undefined || value === '') {
                    value = param.default;
                }

                if (value !== undefined && value !== null) {
                    queryParamsObj[param.name] = value;
                }
            });

            if (Object.keys(queryParamsObj).length > 0) {
                parametersByQuery[query.id] = queryParamsObj;
            }
        });

        onExecute(parametersByQuery);
    };

    const hasDateParams = consolidatedParams.dates.startDate || consolidatedParams.dates.endDate;
    const hasStringParams = Object.keys(consolidatedParams.strings || {}).length > 0;
    const hasNumberParams = Object.keys(consolidatedParams.numbers || {}).length > 0;
    const hasBooleanParams = Object.keys(consolidatedParams.booleans || {}).length > 0;

    if (!isOpen || queries.length === 0) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl max-w-4xl w-full my-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">{t('parameterInputModal.title')}</h3>
                                <p className="text-sm text-gray-600">
                                    {t('parameterInputModal.configureParams', { count: queries.length })}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {/* Date Parameters (Consolidated) */}
                    {hasDateParams && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Calendar className="w-5 h-5 text-primary-600" />
                                <h4 className="font-semibold text-gray-900">{t('parameterInputModal.dateRange')}</h4>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    {t('parameterInputModal.appliedToAll')}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {consolidatedParams.dates.startDate && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('parameterInputModal.startDate')}
                                        </label>
                                        <input
                                            type="date"
                                            value={consolidatedParams.dates.startDate}
                                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {consolidatedParams.dates.endDate && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('parameterInputModal.endDate')}
                                        </label>
                                        <input
                                            type="date"
                                            value={consolidatedParams.dates.endDate}
                                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* String Parameters */}
                    {hasStringParams && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Type className="w-5 h-5 text-primary-600" />
                                <h4 className="font-semibold text-gray-900">{t('parameterInputModal.textParameters')}</h4>
                            </div>
                            <div className="space-y-4">
                                {Object.entries(consolidatedParams.strings).map(([name, defaultValue]) => {
                                    const param = parameters[name];
                                    return (
                                        <div key={name}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                            <input
                                                type="text"
                                                defaultValue={defaultValue}
                                                onChange={(e) => handleStringChange(name, e.target.value)}
                                                placeholder={t('parameterInputModal.placeholderEnter', { name: name.replace(/_/g, ' ') })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                            />
                                            {param && param.queries && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('parameterInputModal.usedIn', { queries: param.queries.join(', ') })}
                                                </p>
                                            )}
                                            {param && param.description && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {param.description}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Number Parameters */}
                    {hasNumberParams && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Hash className="w-5 h-5 text-green-600" />
                                <h4 className="font-semibold text-gray-900">{t('parameterInputModal.numericParameters')}</h4>
                            </div>
                            <div className="space-y-4">
                                {Object.entries(consolidatedParams.numbers).map(([name, defaultValue]) => {
                                    const param = parameters[name];
                                    const paramType = param?.type || 'number';
                                    return (
                                        <div key={name}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                            <input
                                                type="number"
                                                step={paramType === 'float' ? '0.01' : '1'}
                                                defaultValue={defaultValue}
                                                onChange={(e) => handleNumberChange(name, e.target.value)}
                                                placeholder={t('parameterInputModal.placeholderEnter', { name: name.replace(/_/g, ' ') })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                                            />
                                            {param && param.queries && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('parameterInputModal.usedIn', { queries: param.queries.join(', ') })}
                                                </p>
                                            )}
                                            {param && param.description && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {param.description}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Boolean Parameters */}
                    {hasBooleanParams && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <ToggleLeft className="w-5 h-5 text-orange-600" />
                                <h4 className="font-semibold text-gray-900">{t('parameterInputModal.toggleParameters')}</h4>
                            </div>
                            <div className="space-y-3">
                                {Object.entries(consolidatedParams.booleans).map(([name, defaultValue]) => {
                                    const param = parameters[name];
                                    return (
                                        <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex-1">
                                                <label className="text-sm font-medium text-gray-700">
                                                    {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </label>
                                                {param && param.description && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {param.description}
                                                    </p>
                                                )}
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    defaultChecked={defaultValue}
                                                    onChange={(e) => handleBooleanChange(name, e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Query Summary */}
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                        <h4 className="font-semibold text-primary-900 mb-2">Queries to Execute</h4>
                        <ul className="space-y-1">
                            {queries.map((query, idx) => (
                                <li key={query.id} className="text-sm text-primary-800 flex items-start gap-2">
                                    <span className="font-medium">{idx + 1}.</span>
                                    <span>{query.name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExecute}
                        disabled={loading}
                        className={`px-6 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all shadow-sm ${
                            loading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                    >
                        {loading ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                Executing...
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                Execute All Queries
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
